import React, { useState } from "react";
import {
    IconButton,
    Theme,
    StandardProps,
    Menu,
    MenuItem,
    Fade
} from "@material-ui/core";
import defaultImage from "images/default-image.png";

import { createStyles, makeStyles } from "@material-ui/styles";
import pretty from "prettysize";
import { StyleClassKey } from "js/react/typescript";

import {
    AppTableRow,
    AppTableCell,
    TimePresenter,
    AppButton,
    RenderWithConfirmation,
    AppPlyr
} from "js/react/components";
import { MediaContentRendererProps } from "js/react/scenes/components/MediaManagement/Types";
import { singleSelectable } from "js/react/HOC";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { MoreActionMenu } from "js/react/scenes/components/MediaManagement/MoreActionMenu";
import { getImageThumbnail } from "js/services/Media";
import { MimeType } from "js/models";
import { CreativePreviewDialog } from "./CreativePreviewDialog";
import { Typography } from "@material-ui/core";
import { getAssetType } from "js/types";

const styles = (theme: Theme) =>
    createStyles({
        allowed: {},
        root: {
            margin:"5px 0",
            "&:hover $btn$allowed": {
                visibility: "visible"
            },
            "&:hover $menuIcon": {
                visibility: "visible"
            }
        },
        folder: {},
        hover: {},
        tableCell: {
            padding: theme.spacing(1.5, 3),
            textAlign: "left",
            color: theme.palette.grey["700"],
            paddingTop: "0",
            paddingBottom: "0"
        },
        nameCell: {
            display: "flex",
            alignItems: "center",
            "&$folder": {
                cursor: "pointer"
            }
        },
        menuIcon: {
            visibility: "hidden"
        },
        icon: {
            color: theme.palette.common.white,
            fontSize: "2.5rem",
            marginRight: theme.spacing(1)
        },
        btn: {
            visibility: "hidden",
            width: "90px",
            padding: theme.spacing(0.5, 1.5),
            textTransform: "initial",
            fontSize: "0.6rem"
        },
        imgView: {
            width: "90px",
            height: "70px",
            objectFit: "contain",
            objectPosition: "center",
            marginRight: "20px"
        },
        folderIconWrapper: {
            width: "90px",
            height: "70px",
            objectFit: "cover",
            objectPosition: "center",
            marginRight: "20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: theme.palette.primary.main
        },
        displayNameWrapper: {
            textAlign: "left",
            "&:hover": {
                "& span": {
                    //display: "inline"
                    overflow: "visible",
                    display: "block"
                }
            }
        },
        displayName: {
            //clip line after 2 lines

            display: "-webkit-box",
            "-webkit-box-orient": "vertical",
            "-webkit-line-clamp": "2",
            overflow: "hidden",
            maxWidth: "200px",
            wordBreak: "break-all"
        }
    });

type MediaListRowClassKey = StyleClassKey<typeof styles>;

const useStyles = makeStyles(styles);
const SelectableRow = singleSelectable(AppTableRow);

export interface MediaListRowProps
    extends StandardProps<{}, MediaListRowClassKey>,
        MediaContentRendererProps {}

function Component(props: MediaListRowProps) {
    const classes = useStyles(props);
    const { onChosen, isChecked, onChecked, media } = props;

    const [previewToggle, setPreviewToggle] = useState(false);

    const onClosePreview = () => {
        setPreviewToggle(false);
    };

    return (
        <React.Fragment>
            <SelectableRow
                classes={{
                    hover: classes.hover
                }}
                className={classes.root}
                allowChecked
                identifier={media.identifier}
                selection={isChecked ? [media.identifier] : []}
                onSelectionChanged={() => onChecked(media, !isChecked)}
                hover
            >
                {previewToggle && (
                    <CreativePreviewDialog
                        onClose={onClosePreview}
                        title={media.DisplayName}
                        content={media.detail}
                    />
                )}
                <AppTableCell className={classes.tableCell}>
                    <div
                        className={clsx(classes.nameCell, {
                            [classes.folder]: media.type === "folder"
                        })}
                        onClick={() => props.onItemClick(media)}
                    >
                        {media.type === "file" && (
                            <React.Fragment>
                                {/*<FontAwesomeIcon
                            icon={["fal", "photo-video"]}
                            className={classes.icon}
                        />*/}

                                <img
                                    onError={e =>
                                        ((e.target as any).src = defaultImage)
                                    }
                                    className={classes.imgView}
                                    src={
                                        media.thumbnailUrl
                                            ? getImageThumbnail(
                                                  media.thumbnailUrl
                                              )
                                            : defaultImage
                                    }
                                    alt={media.DisplayName}
                                />
                            </React.Fragment>
                        )}
                        {media.type === "folder" && (
                            <div className={classes.folderIconWrapper}>
                                <FontAwesomeIcon
                                    icon={["fas", "folder"]}
                                    className={classes.icon}
                                />
                            </div>
                        )}
                        <div className={classes.displayNameWrapper}>
                            <Typography
                                variant="caption"
                                className={classes.displayName}
                            >
                                {media.DisplayName}
                            </Typography>
                        </div>
                        {/*media.DisplayName.length > 40
                            ? media.DisplayName.slice(0, 39) + "..."
                        : media.DisplayName*/}
                    </div>
                </AppTableCell>
                <AppTableCell className={classes.tableCell}>
                    {media.type === "file" && (
                        <TimePresenter outputFormat={"MMM D, YYYY; h:mm A zZ"}>
                            {media.LastModified}
                        </TimePresenter>
                    )}
                </AppTableCell>
                <AppTableCell
                    className={classes.tableCell}
                    style={{ textAlign: "center" }}
                >
                    {media.type === "file" ? pretty(media.Size) : null}
                </AppTableCell>
                <AppTableCell className={classes.tableCell}>
                    <AppButton
                        buttonVariant="dark"
                        onClick={() => onChosen(media)}
                        className={clsx(classes.btn, {
                            [classes.allowed]:
                                props.selectionType === media.type
                        })}
                    >
                        {media.type === "file"
                            ? "Choose File"
                            : "Choose Folder"}
                    </AppButton>
                </AppTableCell>
                <AppTableCell className={classes.tableCell}>
                    <MoreActionMenu
                        selectionType={props.selectionType}
                        onChosen={props.onChosen}
                        onDelete={props.onDelete}
                        media={props.media}
                        className={classes.menuIcon}
                        onRename={props.onRename}
                        showPreviewIcon={true}
                        setPreviewToggle={setPreviewToggle}
                    />
                </AppTableCell>
            </SelectableRow>
        </React.Fragment>
    );
}

Component.defaultProps = {} as MediaListRowProps;
Component.displayName = "MediaListRow";
export const MediaListRow = Component;
export default MediaListRow;
